// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState, useEffect, useRef } from 'react';
import { Call, LocalAudioStream } from '@azure/communication-calling';
import {
  CallProvider,
  CallClientProvider,
  CallAgentProvider,
  DeclarativeCallAgent,
  StatefulCallClient,
  createStatefulCallClient
} from '@azure/communication-react';
import { AzureCommunicationTokenCredential, CommunicationUserIdentifier } from '@azure/communication-common';
import { v1 as createGUID } from 'uuid';
import { useConfig } from '../../../contexts/ConfigContext';
import { connectCallAutomationToGroupCall } from '../../../utils/callAutomationUtils';
import { interviewAPI } from '../../../services/interview/api';
import { fetchConfig, fetchTokenResponse, AppConfigModel } from '../../../utils/AppUtils';
import VideoProctoring from '../../../components/Interview/VideoProctoring';
import './VoiceInterview.css';
import { useAudioNoiseSuppressionProcessor } from '../../../hooks/useAudioNoiseSuppressionProcessor';
import { validateAudioConstraints, getOptimalAudioConstraints } from '../../../utils/audioConstraintsValidator';

interface VoiceInterviewProps {
  sessionToken: string;
  interviewId?: string;
  candidateName: string;
  jobTitle: string;
  proficiencyLevel?: number;
  interviewDuration?: number;
  tabAudioStream?: MediaStream;
}

type AgentMessage = {
  id: string;
  text: string;
  timestamp: string;
  sender: 'agent' | 'candidate';
  isPartial?: boolean;
};

// Completion Screen Component
const CompletionScreen: React.FC<{ candidateName: string; jobTitle: string }> = ({ candidateName, jobTitle }) => {
  const { config, logo } = useConfig();

  return (
    <div className="voice-interview-completion">
      <div className="voice-interview-completion-content">
        {/* Brand Logo */}
        <div className="voice-interview-completion-brand">
          <img src={logo as string} alt={`${config?.companyName || 'G42'} Logo`} width={25} height={25} />
          <span>Interviewer Host</span>
        </div>

        {/* Checkmark */}
        <div className="voice-interview-completion-checkmark">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>

        {/* Title */}
        <h1 className="voice-interview-completion-title">Interview complete</h1>

        {/* Subtitle */}
        <p className="voice-interview-completion-subtitle">
          Thank you, {candidateName}. You've completed your interview for the {jobTitle} role at{' '}
          {config?.companyName || 'G42'}.
        </p>

        {/* Next Steps Cards */}
        <div className="voice-interview-completion-steps">
          <div className="voice-interview-completion-step">
            <div className="voice-interview-completion-step-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
              </svg>
            </div>
            <div className="voice-interview-completion-step-text">
              <div className="voice-interview-completion-step-title">Your responses are being evaluated</div>
              <div className="voice-interview-completion-step-desc">
                The hiring team will review your interview within 2–3 business days.
              </div>
            </div>
          </div>

          <div className="voice-interview-completion-step">
            <div className="voice-interview-completion-step-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </div>
            <div className="voice-interview-completion-step-text">
              <div className="voice-interview-completion-step-title">Expect an email with next steps</div>
              <div className="voice-interview-completion-step-desc">You'll hear from the team.</div>
            </div>
          </div>

          <div className="voice-interview-completion-step">
            <div className="voice-interview-completion-step-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
            <div className="voice-interview-completion-step-text">
              <div className="voice-interview-completion-step-title">Your data is secure</div>
              <div className="voice-interview-completion-step-desc">
                Recording stored for evaluation only.{' '}
                {/* <span className="voice-interview-completion-link">Request deletion</span> */}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <p className="voice-interview-completion-footer">
          You can now close this window. If you have questions, contact the {config?.companyName || 'G42'} talent team
          directly.
        </p>
      </div>
    </div>
  );
};

// Main Interview Screen Component
const VoiceInterviewContent: React.FC<{
  currentCall: Call;
  transcript: AgentMessage[];
  elapsedTime: number;
  isMuted: boolean;
  isConnected: boolean;
  onToggleMute: () => void;
  onEndCall: () => void;
  transcriptEndRef: React.RefObject<HTMLDivElement>;
  serverCallId: string | undefined;
  interviewId: string | undefined;
  totalTime: number;
  isSkillAssessment: boolean;
  tabAudioStream?: MediaStream;
}> = ({
  currentCall,
  transcript,
  elapsedTime,
  isMuted,
  isConnected,
  onToggleMute,
  onEndCall,
  transcriptEndRef,
  serverCallId,
  interviewId,
  totalTime,
  isSkillAssessment,
  tabAudioStream
}) => {
  const { config, logo } = useConfig();
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="voice-interview-container">
      {/* Video Recording - Renders at top center */}
      {serverCallId && interviewId && currentCall && (
        <VideoProctoring
          sessionId={serverCallId}
          interviewId={interviewId}
          call={currentCall}
          tabAudioStream={tabAudioStream}
        />
      )}

      {/* Header */}
      <header className="voice-interview-header">
        <div className="voice-interview-brand">
          <img src={logo as string} alt={`${config?.companyName || 'G42'} Logo`} width={40} height={40} />
          <span className="voice-interview-brand-text">Interviewer Host</span>
        </div>

        {/* <div className="voice-interview-header-badges">
          <div className="voice-interview-badge voice-interview-badge-recording">
            <span className="voice-interview-recording-dot"></span>
            Recording
          </div>
          <div className="voice-interview-badge voice-interview-badge-proctored">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            Proctored
          </div>
        </div> */}

        <div className="voice-interview-header-right">
          {!isSkillAssessment && (
            <div className="voice-interview-timer-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span className="voice-interview-timer">
                {formatTime(elapsedTime)} / {formatTime(totalTime)}
              </span>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="voice-interview-content">
        {/* Transcript Section */}
        <div className="voice-interview-transcript-section">
          <div className="voice-interview-transcript">
            {transcript.length === 0 && !isConnected && (
              <div className="voice-interview-transcript-empty">
                <p>Waiting to start interview...</p>
              </div>
            )}

            {transcript.map((message) => (
              <div key={message.id} className={`voice-interview-message voice-interview-message-${message.sender}`}>
                <div className="voice-interview-message-avatar">
                  {message.sender === 'agent' ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 011 1v3a1 1 0 01-1 1h-1v1a2 2 0 01-2 2H5a2 2 0 01-2-2v-1H2a1 1 0 01-1-1v-3a1 1 0 011-1h1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2zM7.5 14a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm9 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  )}
                </div>
                <div className="voice-interview-message-bubble">{message.text}</div>
              </div>
            ))}

            <div ref={transcriptEndRef} />
          </div>

          <div className="voice-interview-instructions">
            <p>Speak clearly and answer each question based on your experience.</p>
          </div>
        </div>
      </div>

      {/* Footer Controls */}
      <footer className="voice-interview-footer">
        <button
          className={`voice-interview-mic-btn ${isMuted ? 'voice-interview-mic-muted' : ''}`}
          onClick={onToggleMute}
          disabled={!isConnected}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            {isMuted ? (
              <>
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </>
            ) : (
              <>
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </>
            )}
          </svg>
        </button>

        <div className="voice-interview-listening-bar">
          {isConnected ? (
            <>
              <div className="voice-interview-sound-bars">
                {[...Array(12)].map((_, i) => (
                  <span key={i} className="voice-interview-sound-bar" style={{ animationDelay: `${i * 0.1}s` }} />
                ))}
              </div>
              <span className="voice-interview-listening-text">Listening...</span>
            </>
          ) : (
            <span className="voice-interview-listening-text voice-interview-listening-idle">Not connected</span>
          )}
        </div>

        <div className="voice-interview-action-buttons">
          <button className="voice-interview-end-btn" onClick={onEndCall} disabled={!isConnected}>
            End Interview
          </button>
        </div>
      </footer>
    </div>
  );
};

const VoiceInterview: React.FC<VoiceInterviewProps> = ({
  sessionToken,
  interviewId,
  candidateName,
  jobTitle,
  proficiencyLevel,
  interviewDuration,
  tabAudioStream
}) => {
  // Determine if this is a skill assessment based on proficiencyLevel
  const isSkillAssessment = proficiencyLevel !== undefined;

  // RNNoise noise suppression
  const { isReady: isNoiseFilterReady, applyNoiseFilter } = useAudioNoiseSuppressionProcessor();

  // Communication states
  const [callAgent, setCallAgent] = useState<DeclarativeCallAgent>();
  const [statefulCallClient, setStatefulCallClient] = useState<StatefulCallClient>();
  const [tokenCredential, setTokenCredential] = useState<AzureCommunicationTokenCredential>();
  const [userIdentifier, setUserIdentifier] = useState<CommunicationUserIdentifier>();
  const [currentCall, setCurrentCall] = useState<Call | undefined>(undefined);
  const [clientConfig, setClientConfig] = useState<AppConfigModel>();
  const [serverCallId, setServerCallId] = useState<string | undefined>(undefined);

  // Interview states
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [transcript, setTranscript] = useState<AgentMessage[]>([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showCompletion, setShowCompletion] = useState(false);

  // Refs
  const callAutomationStarted = useRef(false);
  const timerInterval = useRef<ReturnType<typeof setInterval>>();
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const autoStartAttempted = useRef(false);
  const completionNotified = useRef(false);

  // Interview duration in seconds (default to 25 minutes if not provided)
  const totalTime = (interviewDuration || 25) * 60;

  // Initialize ACS client
  useEffect(() => {
    const init = async (): Promise<void> => {
      try {
        const config = await fetchConfig();
        setClientConfig(config);

        const { token, user } = await fetchTokenResponse(interviewId);
        console.log('[VoiceInterview] Token and user fetched');
        setUserIdentifier(user);
        setTokenCredential(new AzureCommunicationTokenCredential(token));
      } catch (error) {
        console.error('[VoiceInterview] Error initializing:', error);
      }
    };
    init();
  }, []);

  // Create stateful call client
  useEffect(() => {
    if (!statefulCallClient && userIdentifier) {
      const statefulClient = createStatefulCallClient({ userId: userIdentifier });
      setStatefulCallClient(statefulClient);
      console.log('[VoiceInterview] Stateful client created');
    }
  }, [userIdentifier, statefulCallClient]);

  // Create call agent
  useEffect(() => {
    if (!callAgent && statefulCallClient && tokenCredential) {
      const createAgent = async (): Promise<void> => {
        const agent = await statefulCallClient.createCallAgent(tokenCredential);
        setCallAgent(agent);
        console.log('[VoiceInterview] Call agent created');
      };
      createAgent();
    }
  }, [callAgent, statefulCallClient, tokenCredential]);

  // Setup audio devices (CRITICAL for audio output)
  useEffect(() => {
    const setupAudioDevices = async (): Promise<void> => {
      if (!statefulCallClient) return;

      try {
        console.log('[VoiceInterview] Setting up audio devices...');
        const deviceManager = await statefulCallClient.getDeviceManager();

        // Request audio permissions
        await deviceManager.askDevicePermission({ audio: true, video: false });
        console.log('[VoiceInterview] ✅ Audio permission granted');

        // Get and select speaker
        const speakers = await deviceManager.getSpeakers();
        console.log(
          '[VoiceInterview] Available speakers:',
          speakers.map((s) => s.name)
        );

        if (speakers.length > 0) {
          await deviceManager.selectSpeaker(speakers[0]);
          console.log('[VoiceInterview] ✅ Selected speaker:', speakers[0].name);
        } else {
          console.warn('[VoiceInterview] ⚠️ No speakers found!');
        }

        // Get microphones for debugging
        const microphones = await deviceManager.getMicrophones();
        console.log(
          '[VoiceInterview] Available microphones:',
          microphones.map((m) => m.name)
        );
      } catch (error) {
        console.error('[VoiceInterview] ❌ Audio device setup error:', error);
      }
    };

    setupAudioDevices();
  }, [statefulCallClient]);

  // Auto-start call when call agent is ready
  useEffect(() => {
    if (callAgent && !autoStartAttempted.current && !currentCall) {
      autoStartAttempted.current = true;
      console.log('[VoiceInterview] Auto-starting call...');
      setIsConnecting(true);

      const startCall = async (): Promise<void> => {
        try {
          // Get microphone with browser-native audio processing enabled
          // This prevents agent voice from being captured (echo cancellation)
          const constraints = getOptimalAudioConstraints();
          const rawStream = await navigator.mediaDevices.getUserMedia(constraints);

          const audioTrack = rawStream.getAudioTracks()[0];
          if (!audioTrack) {
            throw new Error('No audio track found in microphone stream');
          }

          // Validate browser actually applied the constraints
          const validation = validateAudioConstraints(audioTrack);

          // Use browser-native processing (recommended for echo cancellation)
          // Browser AEC works BEFORE audio capture, custom processing works AFTER
          let processedStream = rawStream;
          const useAdditionalNoiseFilter = false; // Set to true only if additional filtering needed

          if (useAdditionalNoiseFilter && isNoiseFilterReady) {
            try {
              processedStream = await applyNoiseFilter(rawStream);
            } catch (filterError) {
              processedStream = rawStream;
            }
          }

          const localAudioStream = new LocalAudioStream(processedStream);

          const call = callAgent.join(
            { groupId: createGUID() },
            {
              audioOptions: {
                localAudioStreams: [localAudioStream],
                muted: false
              }
            }
          );
          setCurrentCall(call);
        } catch (error) {
          console.error('[VoiceInterview] Failed to start call:', error);
          // Fallback: join without custom processing
          try {
            const call = callAgent.join({ groupId: createGUID() });
            setCurrentCall(call);
          } catch (fallbackError) {
            console.error('[VoiceInterview] Fallback join also failed:', fallbackError);
            setIsConnecting(false);
          }
        }
      };

      startCall();
    }
  }, [callAgent, currentCall, isNoiseFilterReady, applyNoiseFilter]);

  // Server-Sent Events for live transcripts
  useEffect(() => {
    console.log(
      '[VoiceInterview] [SSE Setup] currentCall:',
      !!currentCall,
      'clientConfig:',
      !!clientConfig,
      'serverCallId:',
      serverCallId
    );

    if (!currentCall || !clientConfig || !serverCallId) {
      console.log('[VoiceInterview] [SSE Setup] Skipping SSE - missing required values');
      return;
    }

    // Try direct connection to backend (bypassing webpack proxy which may buffer SSE)
    const isDev = window.location.port === '3002' || window.location.port === '3000';
    const eventParams = new URLSearchParams({ serverCallId });
    if (interviewId) eventParams.set('interviewId', interviewId);
    const directUrl = isDev
      ? `http://localhost:8081/api/agentStatusEvents?${eventParams.toString()}`
      : `/api/agentStatusEvents?${eventParams.toString()}`;

    console.log('[VoiceInterview] [SSE] Creating EventSource with URL:', directUrl, '(isDev:', isDev, ')');
    // withCredentials so the candidate access cookie reaches the backend in dev, where the
    // stream connects to :8081 directly instead of through the webpack proxy.
    const eventSource = new EventSource(directUrl, { withCredentials: true });
    console.log(
      '[VoiceInterview] [SSE] EventSource created, readyState:',
      eventSource.readyState,
      '(0=CONNECTING, 1=OPEN, 2=CLOSED)'
    );

    eventSource.onopen = () => {
      console.log('[VoiceInterview] [SSE] ✅ Connection OPENED successfully! ReadyState:', eventSource.readyState);
    };

    eventSource.onmessage = (event) => {
      console.log('[VoiceInterview] [SSE] 📨 Received message from server:', event.data);
      try {
        const serverEvent = JSON.parse(event.data);

        if (serverEvent.type === 'connected') {
          console.log(
            '[VoiceInterview] [SSE] ✅ Connection confirmed by server. ClientId:',
            serverEvent.data?.clientId
          );
        } else if (serverEvent.type === 'agentConnectedStatus') {
          console.log('[VoiceInterview] [SSE] Agent connected status from server:', serverEvent.data);
          const { isConnected } = serverEvent.data;
          callAutomationStarted.current = !!isConnected;
          setIsConnected(isConnected);
          setIsConnecting(!isConnected);
        } else if (serverEvent.type === 'candidateSpeechStarted') {
          console.log('[VoiceInterview] [SSE] Candidate speech started');
          setTranscript((prev) => {
            const updated = [
              ...prev.filter((message) => message.id !== 'candidate-speaking'),
              {
                id: 'candidate-speaking',
                text: 'Listening…',
                timestamp: serverEvent.data?.timestamp ?? new Date().toISOString(),
                sender: 'candidate' as const,
                isPartial: true
              }
            ];
            console.log('[VoiceInterview] [SSE] Updated transcript (candidateSpeechStarted):', updated.length);
            return updated;
          });
        } else if (serverEvent.type === 'liveTranscriptDelta') {
          const delta = serverEvent.data?.delta as string | undefined;
          const segmentId = serverEvent.data?.segmentId as string | undefined;
          console.log('[VoiceInterview] [SSE] liveTranscriptDelta:', {
            delta,
            segmentId,
            serverCallId,
            eventCallId: serverEvent.data?.serverCallId
          });

          if (delta && segmentId && serverEvent.data?.serverCallId === serverCallId) {
            const id = `agent-${segmentId}`;
            setTranscript((prev) => {
              const existingIndex = prev.findIndex((message) => message.id === id);
              if (existingIndex < 0) {
                const updated = [
                  ...prev,
                  {
                    id,
                    text: delta,
                    timestamp: serverEvent.data?.timestamp ?? new Date().toISOString(),
                    sender: 'agent' as const,
                    isPartial: true
                  }
                ];
                console.log('[VoiceInterview] [SSE] Added new delta message, total messages:', updated.length);
                return updated;
              }
              const updated = prev.map((message, index) =>
                index === existingIndex ? { ...message, text: message.text + delta } : message
              );
              console.log('[VoiceInterview] [SSE] Appended to existing delta message');
              return updated;
            });
          }
        } else if (serverEvent.type === 'liveTranscript') {
          const transcript = serverEvent.data?.text as string | undefined;
          const timestamp = (serverEvent.data?.timestamp as string | undefined) ?? new Date().toISOString();
          const speaker = serverEvent.data?.speaker as 'candidate' | 'interviewer' | undefined;
          const segmentId = serverEvent.data?.segmentId as string | undefined;
          console.log('[VoiceInterview] [SSE] liveTranscript:', {
            speaker,
            transcript: transcript?.substring(0, 50),
            segmentId,
            serverCallId,
            eventCallId: serverEvent.data?.serverCallId
          });

          if (
            transcript &&
            segmentId &&
            serverEvent.data?.serverCallId === serverCallId &&
            (speaker === 'candidate' || speaker === 'interviewer')
          ) {
            console.log('[VoiceInterview] [SSE] Processing liveTranscript:', speaker, transcript.substring(0, 50));
            // Map 'interviewer' to 'agent' for UI display
            const sender: 'agent' | 'candidate' = speaker === 'interviewer' ? 'agent' : 'candidate';
            const id = `${sender}-${segmentId}`;

            setTranscript((prev) => {
              const withoutListening = prev.filter((message) => message.id !== 'candidate-speaking');
              const existing = withoutListening.some((message) => message.id === id);
              if (existing) {
                const updated = withoutListening.map((message) =>
                  message.id === id ? { ...message, text: transcript, timestamp, isPartial: false } : message
                );
                console.log('[VoiceInterview] [SSE] Updated existing transcript, total messages:', updated.length);
                return updated;
              }
              const updated: AgentMessage[] = [
                ...withoutListening,
                { id, text: transcript, timestamp, sender, isPartial: false }
              ];
              console.log('[VoiceInterview] [SSE] Added new transcript message, total messages:', updated.length);
              return updated;
            });
          }
        } else if (serverEvent.type === 'interviewCompleted' && serverEvent.data?.serverCallId === serverCallId) {
          console.log('[VoiceInterview] Interview completed by agent; ending call after end note.');
          window.setTimeout(() => {
            void currentCall.hangUp();
          }, 6000);
        }
      } catch (error) {
        console.error('[VoiceInterview] [SSE] Error parsing message data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('[VoiceInterview] [SSE] ❌ EventSource ERROR:', error);
      console.error(
        '[VoiceInterview] [SSE] EventSource readyState:',
        eventSource.readyState,
        '(0=CONNECTING, 1=OPEN, 2=CLOSED)'
      );
      console.error('[VoiceInterview] [SSE] URL was:', directUrl);

      if (eventSource.readyState === EventSource.CLOSED) {
        console.error('[VoiceInterview] [SSE] Connection is CLOSED - will NOT reconnect automatically');
      } else if (eventSource.readyState === EventSource.CONNECTING) {
        console.warn('[VoiceInterview] [SSE] Connection failed but EventSource is attempting to reconnect...');
      }
    };

    return () => {
      console.log(
        '[VoiceInterview] [SSE] 🔌 Cleanup: Closing EventSource connection. ReadyState:',
        eventSource.readyState
      );
      eventSource.close();
    };
  }, [currentCall, clientConfig, serverCallId, interviewId]);

  // Handle call state changes
  useEffect(() => {
    if (!currentCall) return;

    const notifySessionComplete = async (): Promise<void> => {
      if (completionNotified.current) return;
      completionNotified.current = true;
      try {
        await interviewAPI.completeSession(sessionToken);
      } catch (error) {
        console.error('[VoiceInterview] Error notifying session complete:', error);
      }
    };

    const handleStateChange = async (): Promise<void> => {
      try {
        console.log('[VoiceInterview] Call state changed:', currentCall.state);

        if (currentCall.state === 'Connected' && !callAutomationStarted.current) {
          callAutomationStarted.current = true;
          setIsConnecting(true);
          console.log('[VoiceInterview] Call connected, connecting to call automation...');

          try {
            // Get serverCallId first for SSE connection
            const id = await currentCall.info?.getServerCallId();
            console.log('[VoiceInterview] Retrieved serverCallId:', id);
            if (id) {
              setServerCallId(id);
              console.log('[VoiceInterview] Set serverCallId state to:', id);
            } else {
              console.error('[VoiceInterview] ERROR: serverCallId is undefined!');
            }

            await connectCallAutomationToGroupCall(currentCall, interviewId, undefined, proficiencyLevel);
            console.log('[VoiceInterview] Successfully connected to call automation');
            setIsConnected(true);
            setIsConnecting(false);
          } catch (error) {
            console.error('[VoiceInterview] Error connecting to call automation:', error);
            callAutomationStarted.current = false;
            setIsConnecting(false);
          }
        }

        if (currentCall.state === 'Disconnected') {
          console.log('[VoiceInterview] Call disconnected - showing completion screen');

          // Get call end reason to determine termination cause
          const callEndReason = currentCall.callEndReason;
          if (callEndReason) {
            console.log('[VoiceInterview] Call end reason:', {
              code: callEndReason.code,
              subCode: callEndReason.subCode
            });

            // Check if this was a server-initiated termination (expected)
            // ACS Call Automation API uses specific codes when terminating calls:
            // - code 0 with subCode 5854 = Call ended by Call Automation/server bot
            // - code 0 = Normal call termination (various subcodes)
            const isServerTerminated =
              (callEndReason.code === 0 && callEndReason.subCode === 5854) || // Call Automation API termination
              callEndReason.code === 0; // Normal termination

            if (isServerTerminated) {
              console.log('[VoiceInterview] Server-initiated or normal termination detected');
            }
          }

          await notifySessionComplete();
          setIsConnected(false);
          setCurrentCall(undefined);
          callAutomationStarted.current = false;
          if (timerInterval.current) {
            clearInterval(timerInterval.current);
          }
          // Show completion screen
          setShowCompletion(true);
        }
      } catch (error: any) {
        // Catch any errors during state change handling (e.g., accessing call properties)
        // Since we're in a disconnect scenario, treat as normal completion
        console.warn('[VoiceInterview] Error during disconnect handling (treating as normal completion):', error);

        // Always show completion screen to avoid error overlay
        // The call has already ended, so this is the appropriate UX
        await notifySessionComplete();
        setIsConnected(false);
        setCurrentCall(undefined);
        callAutomationStarted.current = false;
        if (timerInterval.current) {
          clearInterval(timerInterval.current);
        }
        setShowCompletion(true);
      }
    };

    currentCall.on('stateChanged', handleStateChange);
    return () => currentCall.off('stateChanged', handleStateChange);
  }, [currentCall, interviewId, proficiencyLevel, sessionToken]);

  // Timer
  useEffect(() => {
    if (isConnected) {
      timerInterval.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else if (timerInterval.current) {
      clearInterval(timerInterval.current);
    }

    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    };
  }, [isConnected]);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  const endCall = async (): Promise<void> => {
    if (currentCall) {
      console.log('[VoiceInterview] Hanging up call...');
      if (!completionNotified.current) {
        completionNotified.current = true;
        await interviewAPI.completeSession(sessionToken);
      }
      await currentCall.hangUp();
    }
  };

  const toggleMute = async (): Promise<void> => {
    if (!currentCall) return;

    try {
      if (isMuted) {
        await currentCall.unmute();
        console.log('[VoiceInterview] Unmuted');
      } else {
        await currentCall.mute();
        console.log('[VoiceInterview] Muted');
      }
      setIsMuted(!isMuted);
    } catch (error) {
      console.error('[VoiceInterview] Error toggling mute:', error);
    }
  };

  // Show completion screen
  if (showCompletion) {
    return <CompletionScreen candidateName={candidateName} jobTitle={jobTitle} />;
  }

  // Show loading state while initializing or if required objects not ready
  if (!currentCall || !statefulCallClient || !callAgent || isConnecting) {
    return (
      <div className="voice-interview-connecting-overlay">
        <div className="voice-interview-spinner" />
        <p>Connecting to AI interviewer...</p>
      </div>
    );
  }

  // Wrap everything in CallProvider for audio to work
  return (
    <CallClientProvider callClient={statefulCallClient}>
      <CallAgentProvider callAgent={callAgent}>
        <CallProvider call={currentCall}>
          <VoiceInterviewContent
            currentCall={currentCall}
            transcript={transcript}
            elapsedTime={elapsedTime}
            isMuted={isMuted}
            isConnected={isConnected}
            onToggleMute={toggleMute}
            onEndCall={endCall}
            transcriptEndRef={transcriptEndRef}
            serverCallId={serverCallId}
            interviewId={interviewId}
            totalTime={totalTime}
            isSkillAssessment={isSkillAssessment}
            tabAudioStream={tabAudioStream}
          />
        </CallProvider>
      </CallAgentProvider>
    </CallClientProvider>
  );
};

export default VoiceInterview;
